import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCircleInfo} from '@fortawesome/pro-solid-svg-icons';
import {BicButtonComponent, BicPopupComponent} from '@buildinginformationcloud/bic-ui-components';
import {ReferencedRuleset} from '../../../../services/workflow.service';

/**
 * Rückfrage beim Duplizieren eines Prüflaufs, der aus einem anderen Projekt in dieses geteilt ist.
 *
 * <p>Die Kopie gehört diesem Projekt. Die myChecks, die hier nicht verfügbar sind, kann sie deshalb
 * nicht einfach weiter verwenden: ausgewählte werden als eigene Kopien angelegt (serverseitig, ohne
 * die Kontextdateien des anderen Projekts), nicht ausgewählte werden aus der Kopie entfernt.
 */
@Component({
  selector: 'app-duplicate-linked-check-prompt',
  standalone: true,
  imports: [FaIconComponent, BicPopupComponent, BicButtonComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './duplicate-linked-check-prompt.component.html',
})
export class DuplicateLinkedCheckPromptComponent implements OnInit {
  @Input({required: true}) checkName!: string;
  /** Hier nicht verfügbare myChecks des Prüflaufs. */
  @Input({required: true}) rulesets!: ReferencedRuleset[];
  @Input() saving = false;

  @Output() closed = new EventEmitter<void>();
  /** IDs der myChecks, die als eigene Kopien mitkommen sollen. */
  @Output() confirmed = new EventEmitter<string[]>();

  protected readonly faCircleInfo = faCircleInfo;
  protected selected = new Set<string>();

  ngOnInit(): void {
    // Voreingestellt: alles mitkopieren – sonst entstünde meist ein leerer Prüflauf. myChecks eines
    // dritten Projekts (shareable=false) darf nur dieses Projekt weitergeben; sie fallen aus der Kopie.
    this.selected = new Set(this.rulesets.filter(r => r.shareable).map(r => r.id));
  }

  protected toggle(ruleset: ReferencedRuleset): void {
    if (this.saving || !ruleset.shareable) return;
    const id = ruleset.id;
    if (this.selected.has(id)) {
      this.selected.delete(id);
    } else {
      this.selected.add(id);
    }
  }

  protected get ownerProjectNames(): string {
    return [...new Set(this.rulesets.map(r => r.ownerProjectName).filter(Boolean))].join(', ') || 'einem anderen Projekt';
  }

  protected onClose(): void {
    if (this.saving) return;
    this.closed.emit();
  }

  protected onConfirm(): void {
    if (this.saving) return;
    this.confirmed.emit([...this.selected]);
  }
}
